// Visitor system: agents enter at the gate, walk the path network, view animals,
// buy from amenities, and leave — their happiness drives reputation.
import * as THREE from 'three';
import { store, pushNews } from '../core/store.js';
import { bus } from '../core/bus.js';
import { AMENITIES } from './amenityCatalog.js';
import { bakeToSingleMesh } from '../core/meshUtils.js';
import { hasPerk } from './gameplay.js';

// Wish balloons: a visitor with an unmet need shows a small emoji bubble.
// Fulfil the wish (sell to them) while it shows = bonus. Shared materials, warmed up at start.
const WISH_EMOJI = { hunger: '\u{1F354}', thirst: '\u{1F964}', toilet: '\u{1F6BB}', fun: '\u{1F388}' };
let WISH_MATS = null;
function getWishMats() {
  if (!WISH_MATS) {
    WISH_MATS = {};
    Object.entries(WISH_EMOJI).forEach(([need, emoji]) => {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const ctx = c.getContext('2d');
      ctx.font = '40px serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.beginPath(); ctx.arc(32, 32, 30, 0, Math.PI * 2); ctx.fillStyle = 'rgba(255,255,255,0.92)'; ctx.fill();
      ctx.fillText(emoji, 32, 36);
      const tex = new THREE.CanvasTexture(c);
      tex.colorSpace = THREE.SRGBColorSpace;
      WISH_MATS[need] = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
    });
  }
  return WISH_MATS;
}

const VISITOR_CACHE = new Map(); // shirt color -> baked single mesh
let GUIDE_CACHE = null;

function buildVisitor() {
  const color = SHIRT_COLORS[Math.floor(Math.random() * SHIRT_COLORS.length)];
  if (!VISITOR_CACHE.has(color)) VISITOR_CACHE.set(color, bakeToSingleMesh(rawBuildVisitor(color)));
  return VISITOR_CACHE.get(color).clone();
}
// A visitor UNIT = a cluster of 1..5 little figures baked into ONE mesh (one draw call, one AI agent).
const GROUP_OFFS = [
  [[0, 0]],
  [[-0.35, 0], [0.35, 0]],
  [[-0.4, -0.2], [0.4, -0.2], [0, 0.35]],
  [[-0.4, -0.3], [0.4, -0.3], [-0.4, 0.3], [0.4, 0.3]],
  [[-0.45, -0.3], [0.45, -0.3], [-0.45, 0.3], [0.45, 0.3], [0, 0.02]],
];
function buildVisitorGroup(n) {
  const g = new THREE.Group();
  const offs = GROUP_OFFS[Math.max(1, Math.min(5, n)) - 1];
  offs.forEach(([ox, oz]) => {
    const color = SHIRT_COLORS[Math.floor(Math.random() * SHIRT_COLORS.length)];
    const fig = rawBuildVisitor(color);
    fig.position.set(ox, 0, oz);
    fig.rotation.y = Math.random() * Math.PI * 2;
    fig.scale.setScalar(0.92 + Math.random() * 0.16);
    g.add(fig);
  });
  return bakeToSingleMesh(g);
}
function buildGuide() {
  if (!GUIDE_CACHE) GUIDE_CACHE = bakeToSingleMesh(rawBuildGuide());
  return GUIDE_CACHE.clone();
}

const MAX_VISITORS = 240;
const RENDER_CAP = 50; // max rendered UNITS; each holds 1-5 figures, then ×N crowd badges stack on top
function makeCrowdBadge(n) {
  const cv = document.createElement('canvas'); cv.width = 128; cv.height = 72;
  const x = cv.getContext('2d');
  x.fillStyle = 'rgba(28,38,48,0.86)';
  x.beginPath(); x.roundRect(14, 12, 100, 48, 14); x.fill();
  x.fillStyle = '#ffe08a'; x.font = 'bold 42px system-ui'; x.textAlign = 'center'; x.textBaseline = 'middle';
  x.fillText('\u00d7' + n, 64, 38);
  const tex = new THREE.CanvasTexture(cv);
  const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
  s.scale.set(1.3, 0.73, 1); s.position.y = 2.5;
  return s;
}
const SEASON_MULT = { spring: 1.0, summer: 1.6, autumn: 0.8, winter: 0.45 };

// Visitor personalities: bias needs, spending and how much they enjoy viewing animals.
const VISITOR_TYPES = [
  { id: 'family', label: 'Family', spend: 1.3, view: 1.0 },
  { id: 'photographer', label: 'Photographer', spend: 0.8, view: 1.6 },
  { id: 'foodie', label: 'Foodie', spend: 1.7, view: 0.8 },
  { id: 'naturalist', label: 'Naturalist', spend: 1.0, view: 1.4 },
];

const SHIRT_COLORS = [0xd94f4f, 0x4f7ad9, 0x4fd98a, 0xd9c44f, 0xb04fd9, 0xd97f4f, 0x4fc3d9, 0xe0e0e0];

function rawBuildVisitor(shirtColor) {
  const g = new THREE.Group();
  const shirt = new THREE.MeshStandardMaterial({ color: shirtColor, roughness: 0.9 });
  const skin = new THREE.MeshStandardMaterial({ color: 0xd9a781, roughness: 0.85 });
  const pants = new THREE.MeshStandardMaterial({ color: 0x3a4152, roughness: 0.9 });
  const legs = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.16, 0.55, 6), pants);
  legs.position.y = 0.28;
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.2, 0.4, 3, 8), shirt);
  body.position.y = 0.9;
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 8, 6), skin);
  head.position.y = 1.45;
  [legs, body, head].forEach((m) => { m.castShadow = true; g.add(m); });
  return g;
}

function rawBuildGuide() {
  const g = new THREE.Group();
  const shirt = new THREE.MeshStandardMaterial({ color: 0x1f8a5b, roughness: 0.85 });
  const skin = new THREE.MeshStandardMaterial({ color: 0xd9a781, roughness: 0.85 });
  const pants = new THREE.MeshStandardMaterial({ color: 0x2c2f38, roughness: 0.9 });
  const legs = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.16, 0.55, 6), pants);
  legs.position.y = 0.28;
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.2, 0.4, 3, 8), shirt);
  body.position.y = 0.9;
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 8, 6), skin);
  head.position.y = 1.45;
  // tour flag
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 1.2, 4), pants);
  pole.position.set(0.3, 1.4, 0);
  const flag = new THREE.Mesh(new THREE.PlaneGeometry(0.35, 0.22), new THREE.MeshStandardMaterial({ color: 0xf4c430, side: THREE.DoubleSide }));
  flag.position.set(0.48, 1.85, 0);
  [legs, body, head, pole, flag].forEach((m) => { m.castShadow = true; g.add(m); });
  return g;
}

export class VisitorSystem {
  constructor(world, build, amenities, animals) {
    this.world = world;
    this.build = build;
    this.amenities = amenities;
    this.animals = animals;
    this.visitors = [];
    this.group = new THREE.Group();
    world.renderer.scene.add(this.group);
    this._spawnTimer = 0;
    this._warnedNoPath = false;

    // Warm-up wish balloon textures/shader (no first-show hitch)
    {
      const mats = getWishMats();
      Object.values(mats).forEach((m) => world.renderer.renderer.initTexture(m.map));
    }
    this._balloons = 0;
    this._placeGate();
    this._subs = [bus.on('sim:newDay', () => this._dailyStats())];
  }
  dispose() { this._subs.forEach((u) => u()); }

  // Gate on the south perimeter fence line; visitors enter from the gate cell.
  _placeGate() {
    const t = this.world.terrain;
    this.gateCell = { cx: Math.floor(t.cells / 2), cz: t.cells - (t.perimeterInset ?? 2) - 1 };
    const half = t.worldSize / 2;
    const wx = (this.gateCell.cx + 0.5) * t.cellSize - half;
    const wz = (this.gateCell.cz + 0.5) * t.cellSize - half;
    const y = t.heightAtWorld(wx, wz);

    const g = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color: 0x8a6642, roughness: 0.8 });
    const beamMat = new THREE.MeshStandardMaterial({ color: 0xc9a86a, roughness: 0.7 });
    for (const dx of [-1, 1]) {
      const pillar = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.45, 6, 8), mat);
      pillar.position.set(dx * 4, 3, 0);
      pillar.castShadow = true;
      g.add(pillar);
    }
    const beam = new THREE.Mesh(new THREE.BoxGeometry(9.6, 1.1, 0.8), beamMat);
    beam.position.y = 6;
    beam.castShadow = true;
    g.add(beam);
    g.position.set(wx, y, wz);
    this.world.renderer.scene.add(g);
    this.gateModel = g;
  }

  // simulation tick (fixed timestep)
  simTick(dt) {
    this._syncGuides();
    const s = store.get();
    // spawn rate: reputation + prestige + season
    const seasonMult = SEASON_MULT[s.season] ?? 1;
    const econMult = this.economy ? this.economy.appealMultiplier() : 1;
    const weatherMult = this.weather ? this.weather.visitorMultiplier() : 1;
    // pricier tickets deter visitors
    const feeFactor = Math.max(0.2, 1.4 - (s.entranceFee ?? 25) / 60);
    const escapePenalty = this.animals.animals.some((a) => a.escaped) ? 0.75 : 1;
    const daylight = this.world.renderer.daylight ?? 1;   // nobody arrives at night
    const appeal = (s.reputation / 100) * (1 + s.prestige / 100);
    const rushMult = (s.weekendRush ? 2.0 : 1) * (hasPerk('marketing') ? 1.15 : 1);
    const perSecond = appeal * seasonMult * econMult * weatherMult * feeFactor * escapePenalty * daylight * rushMult * 1.8;
    this._spawnTimer += dt * perSecond;
    while (this._spawnTimer >= 1) {
      this._spawnTimer -= 1;
      this._trySpawn();
    }
    this._updateVisitors(dt);
    this._updateGuides(dt);
  }

  _syncGuides() {
    this.guides = this.guides || [];
    const want = this.economy ? this.economy.staff.guide : 0;
    while (this.guides.length < want) {
      const model = buildGuide();
      const pos = this._cellWorld(this.gateCell.cx, this.gateCell.cz);
      model.position.copy(pos);
      this.group.add(model);
      this.guides.push({ model, cell: { ...this.gateCell }, prevCell: null, target: null, speed: 1.8 });
    }
    while (this.guides.length > want) {
      const g = this.guides.pop();
      this.group.remove(g.model);
    }
  }

  _updateGuides(dt) {
    if (!this.guides) return;
    for (const g of this.guides) {
      if (!g.target) {
        const options = this._neighbors(g.cell.cx, g.cell.cz);
        if (!options.length) continue;
        const fwd = options.filter((o) => !g.prevCell || o.cx !== g.prevCell.cx || o.cz !== g.prevCell.cz);
        const pool = fwd.length ? fwd : options;
        const next = pool[Math.floor(Math.random() * pool.length)];
        g.prevCell = { ...g.cell };
        g.cell = next;
        g.target = this._cellWorld(next.cx, next.cz);
      }
      const dir = g.target.clone().sub(g.model.position); dir.y = 0;
      const dist = dir.length();
      if (dist < 0.15) { g.target = null; continue; }
      dir.normalize();
      g.model.position.addScaledVector(dir, Math.min(dist, g.speed * dt));
      g.model.position.y = this.world.terrain.heightAtWorld(g.model.position.x, g.model.position.z);
      g.model.rotation.y = Math.atan2(dir.x, dir.z);
      // nearby visitors join the tour: happiness + fun boost
      for (const v of this.visitors) {
        if (v.model.position.distanceToSquared(g.model.position) < 36) {
          v.happiness = Math.min(100, v.happiness + dt * 1.5);
          v.needs.fun = Math.max(0, v.needs.fun - dt * 4);
        }
      }
    }
  }

  _trySpawn() {
    // Ticket sales scale with spawn rate. Rendered UNITS are capped; once full, grow existing
    // groups toward 5 figures, then stack ×N crowd badges on top — attendance is uncapped.
    if (this.visitors.length >= RENDER_CAP) {
      const growable = this.visitors.filter((v) => !v.leaving && (v.groupSize || 1) < 5);
      if (growable.length) {
        this._growUnit(growable[(Math.random() * growable.length) | 0]);
      } else {
        const pool = this.visitors.filter((v) => !v.leaving);
        const list = pool.length ? pool : this.visitors;
        const v = list[(Math.random() * list.length) | 0];
        if (v) { v.mult = (v.mult || 1) + 1; this._updateCrowdBadge(v); }
      }
      store.set({ money: store.get().money + (store.get().entranceFee ?? 25), visitorCount: this._attendance() });
      bus.emit('visitor:entered');
      return;
    }
    const gateKey = `${this.gateCell.cx},${this.gateCell.cz}`;
    if (!this.build.paths.has(gateKey)) {
      if (!this._warnedNoPath) {
        this._warnedNoPath = true;
        bus.emit('ui:toast', { text: 'Build a path at the entrance gate so visitors can come in!', kind: 'error' });
      }
      return;
    }
    const isVIP = Math.random() < 0.04;
    const model = buildVisitorGroup(1);
    if (isVIP) {
      model.traverse((o) => {
        if (o.isMesh) {
          o.material = o.material.clone();
          o.material.color.set(0xd9b23c);
          o.material.emissive = new THREE.Color(0x8a6a1a);
          o.material.emissiveIntensity = 0.4;
        }
      });
      model.scale.setScalar(1.15);
      bus.emit('ui:toast', { text: '🌟 A VIP is visiting your zoo! Keep them happy!', kind: 'success' });
    }
    const t = this.world.terrain;
    const half = t.worldSize / 2;
    const wx = (this.gateCell.cx + 0.5) * t.cellSize - half;
    const wz = (this.gateCell.cz + 0.5) * t.cellSize - half;
    model.position.set(wx, t.heightAtWorld(wx, wz), wz);
    this.group.add(model);

    this.visitors.push({
      model,
      cell: { ...this.gateCell },
      prevCell: null,
      target: null,
      speed: 2.2 + Math.random() * 1.2,
      happiness: 55,
      needs: { hunger: Math.random() * 30, thirst: Math.random() * 30, toilet: Math.random() * 20, fun: 30 + Math.random() * 20 },
      seenEnclosures: new Set(),
      timeInZoo: 0,
      maxTime: 90 + Math.random() * 120,   // sim-seconds
      leaving: false,
      pauseT: 0,
      vip: isVIP,
      vtype: VISITOR_TYPES[(Math.random() * VISITOR_TYPES.length) | 0],
      wish: (() => { const present = [...new Set(this.animals.animals.filter((a) => !a.escaped).map((a) => a.species))]; return (present.length && Math.random() < 0.6) ? present[(Math.random() * present.length) | 0] : null; })(),
      wishDone: false,
      mult: 1,
      groupSize: 1,
    });
    store.set({ money: store.get().money + (store.get().entranceFee ?? 25), visitorCount: this._attendance() });
    bus.emit('visitor:entered');
  }

  // Total attendance = sum over units of (figures in the unit × crowd multiplier).
  _attendance() { return this.visitors.reduce((n, v) => n + (v.groupSize || 1) * (v.mult || 1), 0); }

  // Add a figure to a unit (up to 5), rebuilding its baked cluster mesh in place.
  _growUnit(v) {
    v.groupSize = Math.min(5, (v.groupSize || 1) + 1);
    const pos = v.model.position.clone(), rotY = v.model.rotation.y, scl = v.model.scale.clone();
    if (v.badge) { v.model.remove(v.badge); v.badge = null; }
    this.group.remove(v.model);
    const nm = buildVisitorGroup(v.groupSize);
    nm.position.copy(pos); nm.rotation.y = rotY; nm.scale.copy(scl);
    this.group.add(nm);
    v.model = nm;
    this._updateCrowdBadge(v);
  }

  _updateCrowdBadge(v) {
    if (v.badge) { v.model.remove(v.badge); v.badge = null; }
    if ((v.mult || 1) >= 2) { v.badge = makeCrowdBadge(v.mult); v.model.add(v.badge); }
  }

  _cellWorld(cx, cz) {
    const t = this.world.terrain;
    const half = t.worldSize / 2;
    const wx = (cx + 0.5) * t.cellSize - half;
    const wz = (cz + 0.5) * t.cellSize - half;
    return new THREE.Vector3(wx, t.heightAtWorld(wx, wz), wz);
  }

  _neighbors(cx, cz) {
    return [[1,0],[-1,0],[0,1],[0,-1]]
      .map(([dx,dz]) => ({ cx: cx+dx, cz: cz+dz }))
      .filter((c) => {
        const key = `${c.cx},${c.cz}`;
        // walkable = a path cell that is NOT inside any enclosure (visitors never enter enclosures)
        return this.build.paths.has(key) && !this.build.enclosures.some((e) => e.cells.has(key));
      });
  }

  _updateVisitors(dt) {
    const toRemove = [];
    const guideCells = new Set((this.guides || []).map((g) => g.cell.cx + ',' + g.cell.cz));
    for (const v of this.visitors) {
      v.timeInZoo += dt;
      // Tour guide nearby: guests on a guide's cell gain happiness and have more fun.
      if (guideCells.size && guideCells.has(v.cell.cx + ',' + v.cell.cz)) {
        v.happiness = Math.min(100, v.happiness + dt * 4);
        v.needs.fun = Math.max(0, v.needs.fun - dt * 8);
      }
      if (v.pauseT > 0) { v.pauseT -= dt; continue; }

      // needs grow (capped at 100 so they can't run away)
      v.needs.hunger = Math.min(100, v.needs.hunger + dt * 0.45);
      v.needs.thirst = Math.min(100, v.needs.thirst + dt * 0.6 * (this.weather ? this.weather.thirstMultiplier() : 1));
      v.needs.toilet = Math.min(100, v.needs.toilet + dt * 0.35);

      // Happiness is driven FIRST by guest-need coverage (food/drinks/toilets/fun), then topped off by
      // COMFORT coverage (bins/lighting/seating). Ease each guest toward that baseline.
      if (this.economy) {
        const cov = this.economy.needCoverage();
        const com = this.economy.comfortCoverage();
        const target = 20 + 60 * cov.avg + 20 * com.avg;   // needs → up to 80, comfort tops it to 100
        v.happiness += (target - v.happiness) * Math.min(1, dt * 0.35);
      }
      // local stings: a guest with a screaming unmet need, or a filthy zoo
      for (const k of ['hunger', 'thirst', 'toilet']) { if (v.needs[k] > 92) v.happiness -= dt * 0.5; }
      if (this.economy && this.economy.cleanlinessScore() < 0.6) v.happiness -= dt * 0.4;
      // Happiness is a 0–100 gauge — clamp BOTH ends (an unclamped floor tanked reputation on exit).
      v.happiness = Math.max(0, Math.min(100, v.happiness));

      if (v.timeInZoo > v.maxTime) v.leaving = true;

      if (!v.target) {
        // At a cell: interact, then pick the next one
        this._interact(v);
        const next = this._pickNext(v);
        if (!next) { v.pauseT = 1.5; continue; }
        v.prevCell = { ...v.cell };
        v.cell = next;
        v.target = this._cellWorld(next.cx, next.cz);
        if (this.world.flow) this.world.flow.record(`${next.cx},${next.cz}`, next.cx - v.prevCell.cx, next.cz - v.prevCell.cz);
        // Comfort: nice spots (benches/lamps/fountains) lift happiness, barren long walks wear it down
        if (this.world.props) {
          const c = this.world.props.comfortAt(next.cx, next.cz);
          if (c >= 60) v.happiness = Math.min(100, v.happiness + 0.6);
          else if (c < 15) v.happiness = Math.max(0, v.happiness - 0.15);
        }
      }

      const dir = v.target.clone().sub(v.model.position); dir.y = 0;
      const dist = dir.length();
      if (dist < 0.15) {
        v.target = null;
        if (v.leaving && v.cell.cx === this.gateCell.cx && v.cell.cz === this.gateCell.cz) {
          if (v.vip) {
            if (v.happiness >= 70) {
              store.set({ money: store.get().money + 1000, prestige: store.get().prestige + 5 });
              pushNews('The VIP left delighted — donated $1,000 and praised your zoo! (+5 prestige)', 'success');
              bus.emit('ui:celebrate', { kind: 'mission' });
            } else {
              store.set({ reputation: Math.max(5, store.get().reputation - 3) });
              pushNews('The VIP left unimpressed. Ouch. (-3 reputation)', 'error');
            }
          }
          toRemove.push(v);
        }
        continue;
      }
      dir.normalize();
      const step = Math.min(dist, v.speed * dt);
      v.model.position.addScaledVector(dir, step);
      v.model.position.y = this.world.terrain.heightAtWorld(v.model.position.x, v.model.position.z);
      v.model.rotation.y = Math.atan2(dir.x, dir.z);
    }

    // Wish balloons: spawn on strong unmet needs (max 6 at once), tick down lifetimes
    for (const v2 of this.visitors) {
      if (v2.balloon) {
        v2.balloon.t -= dt;
        if (v2.balloon.t <= 0) { v2.model.remove(v2.balloon.sprite); v2.balloon = null; this._balloons--; }
        continue;
      }
      if (this._balloons >= 6) continue;
      for (const need of ['hunger', 'thirst', 'toilet', 'fun']) {
        if (v2.needs[need] > 65 && Math.random() < 0.008) {
          const sprite = new THREE.Sprite(getWishMats()[need]);
          sprite.scale.set(1.1, 1.1, 1);
          sprite.position.y = 2.2;
          v2.model.add(sprite);
          v2.balloon = { sprite, need, t: 10 };
          this._balloons++;
          break;
        }
      }
    }

    if (toRemove.length) {
      for (const v of toRemove) {
        if (v.balloon) { this._balloons--; v.balloon = null; }
        this.group.remove(v.model);
        this.visitors.splice(this.visitors.indexOf(v), 1);
        // departing visitor nudges reputation toward their happiness; a dirty zoo drags it down
        const s = store.get();
        const clean = this.economy ? this.economy.cleanlinessScore() : 1;
        const delta = (v.happiness - 55) * 0.02 - (1 - clean) * 0.5;
        store.set({ reputation: Math.round(Math.min(100, Math.max(5, s.reputation + delta)) * 10) / 10 });
      }
      store.set({ visitorCount: this._attendance() });
    } else if (store.get().visitorCount !== this._attendance()) {
      store.set({ visitorCount: this._attendance() });
    }
  }

  _interact(v) {
    // Buy from adjacent amenities when needs are moderate-to-high
    for (const need of ['hunger', 'thirst', 'toilet', 'fun']) {
      if (v.needs[need] > 40) {
        const item = this.amenities.findAdjacent(v.cell.cx, v.cell.cz, need);
        if (item && !item.closed) {
          const def = AMENITIES[item.type];
          v.needs[need] = 0;
          v.happiness = Math.min(100, v.happiness + 3 + 4 * (this.economy ? this.economy.buildingHappyMult(item) : 1));
          const inc = Math.round(def.income * (this.research ? this.research.incomeMultiplier() : 1) * (hasPerk('retail') ? 1.2 : 1) * (v.groupSize || 1) * (v.mult || 1) * ((v.vtype && v.vtype.spend) || 1) * (this.economy ? this.economy.buildingIncomeMult(item) : 1));
          store.set({ money: store.get().money + inc });
          bus.emit('ui:sale', { type: item.type, amount: inc, cx: item.cx, cz: item.cz });
          if (v.balloon && v.balloon.need === need) {
            v.model.remove(v.balloon.sprite); v.balloon = null; this._balloons--;
            const bonus = 25;
            store.set({ money: store.get().money + bonus });
            bus.emit('ui:sale', { type: 'wish', amount: bonus, cx: item.cx, cz: item.cz });
            v.happiness = Math.min(100, v.happiness + 8);
          }
          v.pauseT = 1.2;
        }
      }
    }
    // View animals in enclosures adjacent to this path cell
    for (const [dx, dz] of [[1,0],[-1,0],[0,1],[0,-1]]) {
      const key = `${v.cell.cx+dx},${v.cell.cz+dz}`;
      const enc = this.build.enclosures.find((e) => e.cells.has(key));
      if (enc && !v.seenEnclosures.has(enc.id)) {
        v.seenEnclosures.add(enc.id);
        const count = this.animals.animals.filter((a) => a.enclosureId === enc.id).length;
        if (count > 0) {
          const viewMult = (this.research ? this.research.viewingMultiplier() : 1) * ((v.vtype && v.vtype.view) || 1);
          v.happiness = Math.min(100, v.happiness + (8 + count * 2) * viewMult);
          v.needs.fun = Math.max(0, v.needs.fun - 30);
          v.pauseT = 2.0;
          // wishlist fulfilled: seeing the species they hoped for delights them (+ a tip)
          if (!v.wishDone && v.wish && this.animals.animals.some((a) => a.enclosureId === enc.id && a.species === v.wish)) {
            v.wishDone = true;
            v.happiness = Math.min(100, v.happiness + 20);
            const tip = 15 * (v.groupSize || 1) * (v.mult || 1);
            store.set({ money: store.get().money + tip });
            bus.emit('ui:sale', { type: 'wish', amount: tip, cx: v.cell.cx, cz: v.cell.cz });
            if (v.vtype && v.vtype.id === 'photographer') store.set({ prestige: store.get().prestige + 1 });
          }
        }
      }
    }
  }

  _pickNext(v) {
    const options = this._neighbors(v.cell.cx, v.cell.cz);
    if (options.length === 0) return null;
    if (v.leaving) {
      // greedy walk toward gate
      options.sort((a, b) =>
        (Math.abs(a.cx - this.gateCell.cx) + Math.abs(a.cz - this.gateCell.cz)) -
        (Math.abs(b.cx - this.gateCell.cx) + Math.abs(b.cz - this.gateCell.cz))
      );
      return options[0];
    }
    // Momentum walk: strongly prefer continuing in the same direction, so wide
    // paths/plazas are crossed in real lanes instead of dithering randomly.
    const fwd = options.filter((o) => !v.prevCell || o.cx !== v.prevCell.cx || o.cz !== v.prevCell.cz);
    const pool = fwd.length ? fwd : options;
    if (v.prevCell) {
      const hx = v.cell.cx - v.prevCell.cx, hz = v.cell.cz - v.prevCell.cz;
      const weighted = [];
      for (const o of pool) {
        const dx = o.cx - v.cell.cx, dz = o.cz - v.cell.cz;
        // same direction ×6, sideways ×1 (occasional lane change / turn)
        const w = (dx === hx && dz === hz) ? 6 : 1;
        for (let i = 0; i < w; i++) weighted.push(o);
      }
      return weighted[Math.floor(Math.random() * weighted.length)];
    }
    return pool[Math.floor(Math.random() * pool.length)];
  }

  _dailyStats() {
    const s = store.get();
    if (this.visitors.length > 0) {
      const avgHappy = this.visitors.reduce((sum, v) => sum + v.happiness, 0) / this.visitors.length;
      if (avgHappy > 75 && Math.random() < 0.25) {
        pushNews('Guests rave about their day at the zoo!', 'success');
        store.set({ prestige: s.prestige + 2 });
      }
    }
  }
}
